import { format } from 'date-fns';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';
import { EnumHelper } from '~/utils/EnumHelper';
import { convertTimeToMinutes } from '~/components/Request/multiday/utils';
import { type IShiftDetailsFormValues } from '~/components/ShiftRequestRedesign/ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { type IMultiDayJobPayload } from '../types';

interface IBuildJobPayloadsOptions {
  duplicate?: boolean;
}

/**
 * Anything but a positive finite rate means "not captured" and is sent as `null` — never `0`,
 * which the backend would store as a real $0 recommendation.
 */
const toRecommendedRate = (value: number | null): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;

export const buildJobPayloads = (
  formValues: IShiftDetailsFormValues,
  options?: IBuildJobPayloadsOptions,
): IMultiDayJobPayload[] => {
  const professionCode = UserProfession.getCode(formValues.professionType) as string;
  const normalizedCode = UserProfession.getNormalizedCode(professionCode) as string;
  const isSubProfession = EnumHelper.getObjectKeys(UserSubProfession).includes(professionCode);
  // Request-level: one office and one profession share one recommendation, so a per-day rate
  // override never changes it.
  const recommendedRate = toRecommendedRate(formValues.recommendedRate);

  return formValues.dates.map((date) => {
    const dateKey = date.toISOString();
    const override = formValues.shiftOverrides?.[dateKey];

    const arrivalTime = override?.arrivalTime ?? formValues.arrivalTime;
    const departureTime = override?.departureTime ?? formValues.departureTime;
    const mealBreakDuration = override?.mealBreakDuration ?? formValues.mealBreakDuration;
    const isMealBreakPaid = override?.isMealBreakPaid ?? formValues.isMealBreakPaid;
    const rate = override?.rate ?? formValues.rate;
    const specialty = override?.preferredSpecialty ?? formValues.preferredSpecialty;
    const isAssistedHygiene = override?.isAssistedHygiene ?? formValues.isAssistedHygiene;
    const procedures = override?.procedures ?? formValues.procedures;
    const autoConfirm = override?.autoConfirm ?? formValues.autoConfirm;

    return {
      profession: normalizedCode,
      subProfession: isSubProfession ? professionCode : null,
      localDate: format(date, 'yyyy-MM-dd'),
      localStart: arrivalTime,
      localEnd: departureTime,
      break_time: convertTimeToMinutes(mealBreakDuration),
      is_break_paid: isMealBreakPaid,
      rate: rate ?? 0,
      recommendedRate,
      specialty,
      isAssistedHygiene: isAssistedHygiene ?? false,
      procedures,
      autoFill: autoConfirm.eligibleProfessionals,
      autofillOptions: {
        favorite: autoConfirm.favorites,
      },
      ...(options?.duplicate ? { duplicate: true } : {}),
    };
  });
};
